/**
 * 请求工具封装 
 * 统一管理HTTP请求
 */
import axios from 'axios';
import { message, Modal } from 'ant-design-vue';
import apiConfig from '@/config/api';
// 由于router是ES模块，需要在运行时动态导入
let router;

// 常量定义，避免未定义错误
let API_CODE = { SUCCESS: 200, ERROR: 500, UNAUTHORIZED: 401, FORBIDDEN: 403, NOT_FOUND: 404, PARAM_ERROR: 400 };
let STORAGE_KEY = { TOKEN: 'token', USER_INFO: 'user_info' };
let ROUTE = { LOGIN: '/login' };

// 尝试导入常量配置，如果失败则使用默认值
try {
  import('./constants').then(constants => {
    API_CODE = constants.API_CODE || API_CODE;
    STORAGE_KEY = constants.STORAGE_KEY || STORAGE_KEY;
    ROUTE = constants.ROUTE || ROUTE;
  }).catch(err => {
    console.warn('加载常量配置失败，使用默认值', err);
  });
} catch (err) {
  console.warn('加载常量配置失败，使用默认值', err);
}

// 从环境变量读取API基础URL，默认为'/api'
const BASE_API_URL = process.env.VUE_APP_BASE_API || '/api';
console.log('API基础路径:', BASE_API_URL);

// 创建axios实例
const service = axios.create({
  baseURL: apiConfig.baseUrl, // 使用配置文件中的基础URL
  timeout: apiConfig.timeout, // 请求超时时间
  // 暂时移除自定义适配器，避免递归调用问题
  // adapter: config => {
  //   // 自定义适配器逻辑已移除，避免递归调用问题
  // }
})

// 请求计数器，用于管理全局loading
let requestCount = 0
let loadingInstance = null

// 显示全局loading
const showLoading = () => {
  // 可以实现全局loading
}

// 隐藏全局loading
const hideLoading = () => {
  // 可以实现全局loading关闭
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    requestCount++
    if (requestCount === 1) {
      showLoading()
    }

    // 添加日志以跟踪请求
    console.log(`🚀 发送请求: ${config.method.toUpperCase()} ${config.url}`, config)

    // 在请求头中添加token
    const token = localStorage.getItem(STORAGE_KEY.TOKEN)
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // 🔧 强制绕过缓存: 对GET请求添加时间戳参数
    if (config.method === 'get' || config.method === 'GET') {
      config.params = config.params || {}
      config.params._t = Date.now()
      console.log('🔄 添加缓存绕过时间戳:', config.params._t)
    }

    return config
  },
  error => {
    requestCount--
    if (requestCount === 0) {
      hideLoading()
    }
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    requestCount--
    if (requestCount === 0) {
      hideLoading()
    }

    const res = response.data
    console.log(`✅ 响应结果: ${response.config.url}`, res)

    // 统一检查接口返回状态码，兼容200、20000和0（Mock数据返回）
    if (res.code !== API_CODE.SUCCESS && res.code !== 20000 && res.code !== 0) {
      const errorMsg = res.message || '请求失败'
      
      // 根据错误码进行不同处理
      switch (res.code) {
        case API_CODE.UNAUTHORIZED:
          // 登录过期，清除用户信息并重新登录
          Modal.confirm({
            title: '系统提示',
            content: '登录状态已过期，您可以继续留在该页面，或者重新登录',
            okText: '重新登录',
            cancelText: '取消',
            type: 'warning',
            onOk: () => {
              localStorage.removeItem(STORAGE_KEY.TOKEN)
              localStorage.removeItem(STORAGE_KEY.USER_INFO)
              // 尝试异步导入router
              import('@/router').then(routerModule => {
                routerModule.default.push(ROUTE.LOGIN);
              }).catch(() => {
                // 降级处理
                window.location.href = '/login';
              });
            },
            onCancel: () => {}
          })
          break
          
        case API_CODE.FORBIDDEN:
          message.error('没有操作权限')
          break
          
        default:
          message.error(errorMsg)
      }
      
      return Promise.reject(new Error(errorMsg))
    } else {
      // 直接返回数据部分，方便使用
      return res
    }
  },
  error => {
    requestCount--
    if (requestCount === 0) {
      hideLoading()
    }
    
    console.error('Response error:', error)
    
    // HTTP错误状态码处理
    let errorMessage = '网络请求失败'
    const response = error.response
    
    if (response) {
      const { status } = response
      
      switch (status) {
        case API_CODE.PARAM_ERROR:
          errorMessage = '请求参数错误'
          break
        case API_CODE.UNAUTHORIZED:
          errorMessage = '登录已过期，请重新登录'
          // 清除本地存储的用户信息并重定向到登录页
          localStorage.removeItem(STORAGE_KEY.TOKEN)
          localStorage.removeItem(STORAGE_KEY.USER_INFO)
          // 尝试异步导入router
          import('@/router').then(routerModule => {
            routerModule.default.push(ROUTE.LOGIN);
          }).catch(() => {
            // 降级处理
            window.location.href = '/login';
          });
          break
        case API_CODE.FORBIDDEN:
          errorMessage = '没有操作权限'
          break
        case API_CODE.NOT_FOUND:
          errorMessage = '请求的资源不存在'
          break
        case API_CODE.ERROR:
          errorMessage = '服务器错误，请联系管理员'
          break
        default:
          errorMessage = `请求失败(${status})`
      }
    } else {
      // 网络连接错误
      if (error.message.includes('timeout')) {
        errorMessage = '请求超时，请检查网络'
      } else if (error.message.includes('Network Error')) {
        errorMessage = '网络异常，请检查网络连接'
      }
    }
    
    message.error(errorMessage)
    
    return Promise.reject(error)
  }
)

/**
 * 封装GET请求
 * @param {string} url - 请求地址
 * @param {object} params - 请求参数
 * @param {object} config - 额外的配置项
 * @returns {Promise} - 返回Promise对象
 */
function get(url, params = {}, config = {}) {
  return service({
    url,
    method: 'get',
    params,
    ...config
  })
}

/**
 * 封装POST请求
 * @param {string} url - 请求地址
 * @param {object} data - 请求数据
 * @param {object} config - 额外的配置项
 * @returns {Promise} - 返回Promise对象
 */
function post(url, data = {}, config = {}) {
  return service({
    url,
    method: 'post',
    data,
    ...config
  })
}

/**
 * 封装PUT请求
 * @param {string} url - 请求地址
 * @param {object} data - 请求数据
 * @param {object} config - 额外的配置项
 * @returns {Promise} - 返回Promise对象
 */
function put(url, data = {}, config = {}) {
  return service({
    url,
    method: 'put',
    data,
    ...config
  })
}

/**
 * 封装DELETE请求
 * @param {string} url - 请求地址
 * @param {object} params - 请求参数
 * @param {object} config - 额外的配置项
 * @returns {Promise} - 返回Promise对象
 */
function del(url, params = {}, config = {}) {
  return service({
    url,
    method: 'delete',
    params,
    ...config
  })
}

/**
 * 上传文件
 * @param {string} url - 上传地址
 * @param {FormData} data - 表单数据
 * @param {Function} onProgress - 上传进度回调
 * @returns {Promise} - 返回Promise对象
 */
function upload(url, data, onProgress = null) {
  return service({
    url,
    method: 'post',
    data,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onProgress ? progressEvent => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
      onProgress(percentCompleted)
    } : null
  })
}

/**
 * 下载文件
 * @param {string} url - 下载地址
 * @param {object} params - 请求参数
 * @param {string} filename - 文件名
 * @returns {Promise} - 返回Promise对象
 */
function download(url, params = {}, filename = '') {
  return service({
    url,
    method: 'get',
    params,
    responseType: 'blob'
  }).then(response => {
    // 创建blob链接
    const blob = new Blob([response.data])
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    
    // 设置下载文件名
    link.download = filename || response.headers['content-disposition']?.split('filename=')[1] || 'download'
    link.click()
    URL.revokeObjectURL(link.href)
    return response
  })
}

// 导出请求函数
// module.exports = service
// module.exports.get = get 
// module.exports.post = post
// module.exports.put = put
// module.exports.delete = del
// module.exports.upload = upload
// module.exports.download = download

// 改为ES Module导出语法
export default service;
export { 
  get,
  post,
  put,
  del as delete,
  upload,
  download
}; 